import React, { useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useNotifications } from '../../providers/notification-provider';
import { Notification } from '../../types/notification';
import { ScrollArea } from './scroll-area';
import { cn } from '../../lib/utils';

interface NotificationDropdownProps {
  onClose: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ 
  onClose 
}) => {
  const { notifications, loading, markNotificationAsRead, refreshNotifications } = useNotifications();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const refreshedRef = useRef(false);

  useEffect(() => {
    // Only refresh once when dropdown opens to avoid infinite loops
    if (!refreshedRef.current) {
      refreshNotifications();
      refreshedRef.current = true;
    }
    
    // Handle clicks outside to close dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      refreshedRef.current = false; // Reset refresh flag when dropdown closes
    };
  }, [refreshNotifications, onClose]);

  const handleItemClick = async (notification: Notification) => {
    if (!notification.read) {
      await markNotificationAsRead(notification.id);
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (e) {
      return 'recently';
    }
  };

  return (
    <div 
      ref={dropdownRef}
      className={cn(
        "absolute right-0 mt-2 w-80 z-50 rounded-md shadow-lg",
        "bg-popover border border-border animate-in fade-in-0 zoom-in-95",
        "origin-top-right overflow-hidden"
      )}
    >
      <div className="px-4 py-3 border-b border-border">
        <h3 className="text-sm font-medium">Notifications</h3>
      </div>
      
      <ScrollArea className="max-h-80">
        {loading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Loading notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No notifications yet
          </div>
        ) : (
          <div className="py-1">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleItemClick(notification)}
                className={cn(
                  "px-4 py-3 text-sm cursor-pointer transition-colors relative",
                  "hover:bg-accent/50 border-b border-border last:border-0",
                  !notification.read && "bg-accent/20 pl-5"
                )}
              >
                {!notification.read && (
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-primary" />
                )}
                <div className={cn(
                  "font-medium", 
                  !notification.read && "text-primary font-semibold"
                )}>
                  {notification.content}
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex justify-between items-center">
                  <span>{formatTime(notification.created_at)}</span>
                  {!notification.read && (
                    <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">
                      NEW
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}; 